import { RadioChangeEvent } from 'antd/es/radio/interface';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { Col, Divider, Flex, Row } from 'antd';
import { useFormContext, useWatch } from 'react-hook-form';
import {
  ClassifierFormDataType,
  ClassifierValidFormDataType,
  FormCheckbox,
  FormDateRangePicker,
  FormField,
  FormInput,
  FormInputMessages,
  FormRadioGroup,
  getOptionsFromSystemClassifierItems,
  Padding,
  useClassifierDataTypesBy,
} from '@atlas-frontend/sdk';
import { CheckboxChangeEvent } from 'antd/es/checkbox';
import { useClassifierFormLabelTranslation } from './useClassifierFormLabelTranslation';

export const ClassifierFormLeftSideFields = ({
  mode,
}: {
  mode: 'create' | 'edit' | 'clone';
}) => {
  const { t } = useClassifierFormLabelTranslation();
  const form = useFormContext<ClassifierFormDataType>();
  const inUse = form.getValues('inUse');

  return (
    <>
      <Padding top horizontal>
        <Row gutter={16}>
          <Col span={8}>
            <FormField name="code" label={t('code.label')} required>
              <FormInput
                name="code"
                disabled={mode === 'clone' ? false : inUse}
              />
            </FormField>
          </Col>
          <Col span={16}>
            <FormField
              name="name"
              label={t('name.label')}
              placeholder={t('codeFormat.placeholder')}
              required
            />
          </Col>
        </Row>
      </Padding>
      <Divider />
      <Padding horizontal>
        <Row>
          <Col span={24}>
            <FormField name="validPeriod" label={t('validPeriod.label')}>
              <FormDateRangePicker
                name="validPeriod"
                allowEmpty={[true, true]}
              />
              <FormInputMessages name="validPeriod.0" />
            </FormField>
          </Col>
        </Row>
      </Padding>
      <Divider />
      <Padding horizontal>
        <Flex vertical gap="large">
          <FormField name="hasCategories">
            <HasCategoriesCheckbox />
          </FormField>
          <CodeFormatFormField />
          <FormField
            id="classifier-description"
            name="description"
            label={t('description.label')}
          >
            <FormInput
              id="classifier-description"
              name="description"
              rows={3}
            />
          </FormField>
        </Flex>
      </Padding>
    </>
  );
};

const HasCategoriesCheckbox: FC = () => {
  const form = useFormContext<ClassifierFormDataType>();
  const inUse = form.getValues('inUse');
  const { t } = useClassifierFormLabelTranslation();

  const [categories, categoryGroups, combinedGroups] =
    useWatch<ClassifierFormDataType>({
      name: ['categories', 'categoryGroups', 'combinedGroups'],
    }) as [
      ClassifierFormDataType['categories'],
      ClassifierFormDataType['categoryGroups'],
      ClassifierFormDataType['combinedGroups']
    ];

  const isCheckboxDisabled = useMemo(
    () =>
      !!(
        (categories?.length || 0) +
        (categoryGroups?.length || 0) +
        (combinedGroups?.length || 0)
      ),
    [categories?.length, categoryGroups?.length, combinedGroups?.length]
  );

  const isDisabled = isCheckboxDisabled || inUse;

  const handleCheckboxChange = useCallback(
    (event: CheckboxChangeEvent) => {
      if (event.target.checked) {
        form.setValue('codeFormat', '', { shouldDirty: true });
        form.setValue('dataTypeId', null, { shouldDirty: true });
      }
    },
    [form]
  );

  return (
    <FormCheckbox
      name="hasCategories"
      disabled={isDisabled}
      onChange={handleCheckboxChange}
    >
      {t('hasCategories.label')}
    </FormCheckbox>
  );
};

type ClassifierDataTypeCode = 'TEXT' | 'DATE' | 'NUMBER';

interface ClassifierDataTypeOptionType {
  code: ClassifierDataTypeCode;
  value: number;
  label: string;
}

const CodeFormatFormField = () => {
  const { t } = useClassifierFormLabelTranslation();
  const hasCategories = useWatch<ClassifierValidFormDataType>({
    name: 'hasCategories',
  }) as boolean;
  const dataTypeCode = useWatch<ClassifierValidFormDataType>({
    name: 'dataTypeCode',
  }) as string;

  const classifierDataTypesById = useClassifierDataTypesBy('id');
  const options = getOptionsFromSystemClassifierItems([
    ...classifierDataTypesById.values(),
  ]) as ClassifierDataTypeOptionType[];
  const defaultDataType = useMemo(
    () => options.find((item) => item.code === 'TEXT')!,
    [options]
  );
  const { getValues, setValue } = useFormContext<ClassifierValidFormDataType>();

  const handleChange = useCallback(
    (event: RadioChangeEvent) => {
      const newCode = classifierDataTypesById.get(event.target.value)!
        .code as ClassifierDataTypeCode;
      setValue('dataTypeCode', newCode);

      if (newCode === 'NUMBER') {
        setValue('codeFormat', '');
      }
    },
    [setValue, classifierDataTypesById]
  );

  useEffect(() => {
    if (!hasCategories) {
      const dataTypeId = getValues('dataTypeId');
      if (!dataTypeId) {
        setValue('dataTypeId', defaultDataType.value);
        setValue('dataTypeCode', defaultDataType.code);
      } else {
        setValue(
          'dataTypeCode',
          classifierDataTypesById.get(dataTypeId)!
            .code as ClassifierDataTypeCode
        );
      }
    }
  }, [
    getValues,
    setValue,
    hasCategories,
    defaultDataType,
    classifierDataTypesById,
  ]);

  if (hasCategories) {
    return null;
  }

  return (
    <>
      <FormField name="dataTypeId" required label={t('dataTypeId.label')}>
        <FormRadioGroup
          name="dataTypeId"
          options={options}
          onChange={handleChange}
        />
      </FormField>
      {dataTypeCode !== 'NUMBER' && (
        <FormField
          label={t('codeFormat.label')}
          name="codeFormat"
          required
          placeholder={t('codeFormat.placeholder')}
          deps="hasCategories"
        />
      )}
    </>
  );
};
